export interface ContributionAllocationForDisplay {
  id?: string | null;
  amount?: number | string | null;
  fund?: {
    name?: string | null;
  } | null;
}

const allocationAmount = (value: ContributionAllocationForDisplay["amount"]) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const allocationFundName = (
  allocation: ContributionAllocationForDisplay,
  fallback: string,
) => allocation.fund?.name?.trim() || fallback;

const fundNameCollator = new Intl.Collator("en-US", {
  numeric: true,
  sensitivity: "base",
});

const compareStrings = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

const compareFundNames = (a: string, b: string) => {
  const collatorDiff = fundNameCollator.compare(a, b);
  if (collatorDiff !== 0) return collatorDiff;
  return compareStrings(a, b);
};

const aggregateAllocationFundNames = (
  allocations: readonly ContributionAllocationForDisplay[],
  fallback: string,
) => {
  const totalsByName = new Map<string, number>();

  for (const allocation of allocations) {
    const name = allocationFundName(allocation, fallback);
    totalsByName.set(
      name,
      (totalsByName.get(name) ?? 0) + allocationAmount(allocation.amount),
    );
  }

  return [...totalsByName.entries()]
    .sort(([nameA, amountA], [nameB, amountB]) => {
      const amountDiff = amountB - amountA;
      if (amountDiff !== 0) return amountDiff;

      return compareFundNames(nameA, nameB);
    })
    .map(([name]) => name);
};

export function sortContributionAllocations<T extends ContributionAllocationForDisplay>(
  allocations: readonly T[] | null | undefined,
): T[] {
  return [...(allocations ?? [])].sort((a, b) => {
    const amountDiff = allocationAmount(b.amount) - allocationAmount(a.amount);
    if (amountDiff !== 0) return amountDiff;

    const nameDiff = compareFundNames(
      allocationFundName(a, ""),
      allocationFundName(b, ""),
    );
    if (nameDiff !== 0) return nameDiff;

    return String(a.id ?? "").localeCompare(String(b.id ?? ""));
  });
}

export function summarizeContributionAllocations(
  allocations: readonly ContributionAllocationForDisplay[] | null | undefined,
  options: {
    emptyLabel?: string;
    fallbackFundName?: string;
    maxNames?: number;
  } = {},
): string {
  const emptyLabel = options.emptyLabel ?? "Unallocated";
  const fallbackFundName = options.fallbackFundName ?? "Unassigned";
  const maxNames = Math.max(1, options.maxNames ?? 2);
  if (!allocations?.length) return emptyLabel;

  const names = aggregateAllocationFundNames(allocations, fallbackFundName);
  const visibleNames = names.slice(0, maxNames);
  const remaining = names.length - visibleNames.length;

  return remaining > 0
    ? `${visibleNames.join(", ")} +${remaining} more`
    : visibleNames.join(", ");
}
